import { Component, OnInit, OnDestroy, ViewChild, ElementRef, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { MenuModule } from 'primeng/menu';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { MenuItem } from 'primeng/api';
import Chart from 'chart.js/auto';
import { Subject, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { EngagementAnalytics, RecentOrder, ShopAnalytics, ShopAnalyticsService, TicketAnalytics, TopProduct } from 'src/app/services/shop-analytics.service';
import { SmartCoinAnalyticsService, SmartCoinChartData, SmartCoinOverview, SmartCoinTransaction } from 'src/app/services/smartcoin-analytics.service';
import { DonationAnalyticsService } from 'src/app/services/donation-analytics.service';
import { BadgeAnalyticsService, BadgeConfig, BadgeOverview, TopBadgeEarners } from 'src/app/services/badge-analytics.service';
import { AlertComponent } from 'src/app/shared/alert/alert.component';
import { EsgHeroComponent } from '../components/esg-hero/esg-hero.component';
import { ESGScore } from 'src/app/shared/models/esg.model';

interface PostStats {
  total_posts: number;
  total_likes: number;
  total_comments: number;
  total_views: number;
  total_shares: number;
  engagement_rate: number;
}

@Component({
  selector: 'app-business-hub-dashboard',
  templateUrl: './home.page.html',
  styleUrls: ['./home.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    ButtonModule,
    TagModule,
    TableModule,
    SkeletonModule,
    MenuModule,
    BreadcrumbModule,
    TabViewModule,
    AlertComponent,
    EsgHeroComponent
  ]
})
export class HomePage implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Chart Refs
  @ViewChild('revenueChart') revenueChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('smartCoinBalanceChart') smartCoinBalanceChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('smartCoinFlowChart') smartCoinFlowChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('engagementChart') engagementChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('funnelChart') funnelChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('sessionDurationChart') sessionDurationChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('donationChart') donationChartRef!: ElementRef<HTMLCanvasElement>;

  // Breadcrumb
  home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard' };
  breadcrumbItems: MenuItem[] = [
    { label: 'Business Hub', routerLink: '/dashboard/business-overview' }
  ];

  // Data
  salesData: ShopAnalytics | null = null;
  engagementData: EngagementAnalytics | null = null;
  smartCoinOverview: SmartCoinOverview | null = null;
  smartCoinChartData: SmartCoinChartData | null = null;
  smartCoinTransactions: SmartCoinTransaction[] = [];
  donationChartData: any | null = null;
  postStats: PostStats | null = null;
  topProducts: TopProduct[] = [];
  recentOrders: RecentOrder[] = [];

  // Badge Data
  badgeOverview: BadgeOverview | null = null;
  badgeConfigs: BadgeConfig[] = [];
  topBadgeEarners: TopBadgeEarners | null = null;

  // Ticket Data
  ticketAnalytics: TicketAnalytics | null = null;

  // ESG Score
  esgScore: ESGScore = {
    environment: 75,
    social: 70,
    governance: 70
  };

  // Loading States
  loadingSales = signal(true);
  loadingEngagement = signal(true);
  loadingSmartCoins = signal(true);
  loadingSmartCoinChart = signal(true);
  loadingSmartCoinTx = signal(true);
  loadingDonations = signal(false);
  loadingPosts = signal(true);
  loadingTopProducts = signal(true);
  loadingRecentOrders = signal(true);
  loadingBadges = signal(true);
  loadingBadgeConfigs = signal(true);
  loadingTopBadgeEarners = signal(true);
  loadingTickets = signal(true);

  // State
  hasError = signal(false);
  selectedPeriod = '30d';
  activeTabIndex = 0;
  isDonationHub = false;

  // Charts
  revenueChart: Chart | null = null;
  smartCoinBalanceChart: Chart | null = null;
  smartCoinFlowChart: Chart | null = null;
  engagementChart: Chart | null = null;
  funnelChart: Chart | null = null;
  sessionDurationChart: Chart | null = null;
  donationChart: Chart | null = null;

  // Menu
  menuItems: MenuItem[] = [
    {
      label: 'Aktualisieren',
      icon: 'pi pi-refresh',
      command: () => this.forceRefresh()
    },
    { separator: true },
    {
      label: '7 Tage',
      icon: 'pi pi-calendar',
      command: () => this.changePeriod('7d')
    },
    {
      label: '30 Tage',
      icon: 'pi pi-calendar',
      command: () => this.changePeriod('30d')
    },
    {
      label: '90 Tage',
      icon: 'pi pi-calendar',
      command: () => this.changePeriod('90d')
    }
  ];

  constructor(
    private shopAnalytics: ShopAnalyticsService,
    private smartCoinAnalytics: SmartCoinAnalyticsService,
    private donationAnalytics: DonationAnalyticsService,
    private badgeAnalytics: BadgeAnalyticsService,
    private http: HttpClient,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.checkIfDonationHub();
    this.loadAllData(false);
  }

  ngOnDestroy(): void {
    this.destroyCharts();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkIfDonationHub(): void {
    this.http.get<any>(`${environment.apiUrl}/business/hubs/my`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.isDonationHub = data.businessType == 'E.V.';
          if (this.isDonationHub) {
            this.loadDonationData(false);
          }
        },
        error: (err) => {
          console.error('Error checking hub type:', err);
          this.isDonationHub = false;
        }
      });
  }

  loadAllData(forceRefresh: boolean = false): void {
    this.resetLoadingStates(true);
    this.hasError.set(false);

    // Sales
    this.shopAnalytics.getAnalytics(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.salesData = data;
          this.loadingSales.set(false);
          setTimeout(() => this.initRevenueChart(), 100);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading sales:', err);
          this.loadingSales.set(false);
        }
      });

    // Engagement
    this.shopAnalytics.getEngagementAnalytics(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.engagementData = data;
          this.loadingEngagement.set(false);
          setTimeout(() => this.initEngagementCharts(), 100);
          this.updateEsgScore();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading engagement:', err);
          this.loadingEngagement.set(false);
        }
      });

    // SmartCoin Overview
    this.smartCoinAnalytics.getOverview(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.smartCoinOverview = data;
          this.loadingSmartCoins.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading SmartCoin overview:', err);
          this.loadingSmartCoins.set(false);
        }
      });

    // SmartCoin Chart
    this.smartCoinAnalytics.getChartData(this.selectedPeriod, true, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.smartCoinChartData = data;
          this.loadingSmartCoinChart.set(false);
          setTimeout(() => this.initSmartCoinCharts(), 100);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading SmartCoin chart:', err);
          this.loadingSmartCoinChart.set(false);
        }
      });

    // SmartCoin Transactions
    this.smartCoinAnalytics.getTransactions(15, 'all', forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.smartCoinTransactions = data.transactions;
          this.loadingSmartCoinTx.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading transactions:', err);
          this.loadingSmartCoinTx.set(false);
        }
      });

    // Donation Chart (only for e.V. hubs)
    if (this.isDonationHub) {
      this.loadDonationData(forceRefresh);
    }

    // Top Products
    this.shopAnalytics.getTopProducts(5, this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.topProducts = data.products;
          this.loadingTopProducts.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading top products:', err);
          this.loadingTopProducts.set(false);
        }
      });

    // Recent Orders
    this.shopAnalytics.getRecentOrders(10, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.recentOrders = data.orders;
          this.loadingRecentOrders.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading recent orders:', err);
          this.loadingRecentOrders.set(false);
        }
      });

    // Post Stats (optional)
    this.http.get<any>(`${environment.apiUrl}/business/posts/stats`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.postStats = data.overview;
          this.loadingPosts.set(false);
          this.cdr.detectChanges();
        },
        error: () => {
          this.loadingPosts.set(false);
        }
      });

    // Badge Analytics
    this.loadBadgeData(forceRefresh);

    // Ticket Analytics
    this.loadTicketData(forceRefresh);
  }

  private loadTicketData(forceRefresh: boolean): void {
    this.shopAnalytics.getTicketAnalytics(forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.ticketAnalytics = data.analytics;
          this.loadingTickets.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading ticket analytics:', err);
          this.loadingTickets.set(false);
        }
      });
  }

  private loadBadgeData(forceRefresh: boolean): void {
    // Badge Overview
    this.badgeAnalytics.getOverview(forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.badgeOverview = data;
          this.loadingBadges.set(false);
          this.updateEsgScore();
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading badge overview:', err);
          this.loadingBadges.set(false);
        }
      });

    // Badge Configs
    this.badgeAnalytics.getAllConfigs(forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.badgeConfigs = data.configs.slice(0, 3); // Top 3 configs
          this.loadingBadgeConfigs.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading badge configs:', err);
          this.loadingBadgeConfigs.set(false);
        }
      });

    // Top Badge Earners
    this.badgeAnalytics.getTopEarners(5, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.topBadgeEarners = data;
          this.loadingTopBadgeEarners.set(false);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading top badge earners:', err);
          this.loadingTopBadgeEarners.set(false);
        }
      });
  }

  private loadDonationData(forceRefresh: boolean): void {
    this.loadingDonations.set(true);
    this.donationAnalytics.getChartData(this.selectedPeriod, forceRefresh)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.donationChartData = data && data.historical ? data : { ...data, historical: [] };
          this.loadingDonations.set(false);
          setTimeout(() => this.initDonationChart(), 100);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading donation data:', err);
          this.donationChartData = { historical: [], totalAmountInEuros: 0, averageDonationInEuros: 0, totalDonors: 0 };
          this.loadingDonations.set(false);
        }
      });
  }

  resetLoadingStates(value: boolean): void {
    this.loadingSales.set(value);
    this.loadingEngagement.set(value);
    this.loadingSmartCoins.set(value);
    this.loadingSmartCoinChart.set(value);
    this.loadingSmartCoinTx.set(value);
    this.loadingPosts.set(value);
    this.loadingTopProducts.set(value);
    this.loadingRecentOrders.set(value);
    this.loadingBadges.set(value);
    this.loadingBadgeConfigs.set(value);
    this.loadingTopBadgeEarners.set(value);
    this.loadingTickets.set(value);
    if (this.isDonationHub) {
      this.loadingDonations.set(value);
    }
  }

  forceRefresh(): void {
    this.destroyCharts();
    this.loadAllData(true);
  }

  changePeriod(period: string): void {
    this.selectedPeriod = period;
    this.destroyCharts();
    this.loadAllData(true);
  }

  // ========================================
  // CHART INITIALIZATION
  // ========================================

  private destroyChart(chart: Chart | null): void {
    if (chart) {
      try {
        chart.destroy();
      } catch (err) {
        console.warn('Chart destroy failed:', err);
      }
    }
  }

  initRevenueChart(): void {
    if (!this.revenueChartRef?.nativeElement || !this.salesData) return;

    this.destroyChart(this.revenueChart);
    this.revenueChart = null;

    const canvas = this.revenueChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    this.shopAnalytics.getMetrics(this.selectedPeriod, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe(metrics => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';
        const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color') || '#e2e8f0';

        const labels = metrics.revenue.map((item: any) => {
          const date = new Date(item.date);
          return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
        });

        this.revenueChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: labels,
            datasets: [{
              label: 'Umsatz (€)',
              data: metrics.revenue.map((item: any) => item.value),
              borderColor: '#10b981',
              backgroundColor: 'rgba(16, 185, 129, 0.08)',
              borderWidth: 2.5,
              tension: 0.4,
              fill: true,
              pointRadius: 0,
              pointHoverRadius: 6,
              pointHoverBackgroundColor: '#10b981',
              pointHoverBorderColor: '#ffffff',
              pointHoverBorderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              intersect: false,
              mode: 'index'
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                titleColor: '#f1f5f9',
                bodyColor: '#cbd5e1',
                padding: 12,
                borderColor: '#334155',
                borderWidth: 1,
                displayColors: false,
                callbacks: {
                  label: (context) => `${context.parsed.y!.toFixed(2)} €`
                }
              }
            },
            scales: {
              x: {
                ticks: {
                  color: textColor,
                  font: { size: 11 }
                },
                grid: {
                  display: false
                },
                border: {
                  display: false
                }
              },
              y: {
                beginAtZero: true,
                ticks: {
                  color: textColor,
                  font: { size: 11 },
                  callback: (value) => `${(value as number).toFixed(2)} €`
                },
                grid: {
                  color: surfaceBorder,
                },
                border: {
                  display: false
                }
              }
            }
          }
        });
      });
  }

  initSmartCoinCharts(): void {
    if (!this.smartCoinChartData) return;
    this.initSmartCoinBalanceChart();
    this.initSmartCoinFlowChart();
  }

  initSmartCoinBalanceChart(): void {
    if (!this.smartCoinBalanceChartRef?.nativeElement || !this.smartCoinChartData) return;

    this.destroyChart(this.smartCoinBalanceChart);
    this.smartCoinBalanceChart = null;

    const canvas = this.smartCoinBalanceChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color') || '#e2e8f0';

    const historical = this.smartCoinChartData.historical;
    const labels = historical.map(item => {
      if (item.date) {
        const date = new Date(item.date);
        return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
      } else if (item.month) {
        const [year, month] = item.month.split('-');
        return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
      }
      return '';
    });

    // ✅ Round to 2 decimal places
    const balanceData = historical.map(item => Math.round((item.balance_end / 100) * 100) / 100);

    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, 'rgba(139, 92, 246, 0.12)');
    gradient.addColorStop(1, 'rgba(139, 92, 246, 0)');

    this.smartCoinBalanceChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Kontostand (€)',
          data: balanceData,
          borderColor: '#8b5cf6',
          backgroundColor: gradient,
          borderWidth: 2.5,
          tension: 0.4,
          fill: true,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#8b5cf6',
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1,
            displayColors: false,
            callbacks: {
              label: (context) => `Kontostand: ${context.parsed.y!.toFixed(2)} €`
            }
          }
        },
        scales: {
          x: {
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            ticks: {
              color: textColor,
              font: { size: 11 },
              callback: (value) => `${(value as number).toFixed(2)} €`
            },
            grid: {
              color: surfaceBorder,
            },
            border: {
              display: false
            }
          }
        }
      }
    });
  }

  initSmartCoinFlowChart(): void {
    if (!this.smartCoinFlowChartRef?.nativeElement || !this.smartCoinChartData) return;

    this.destroyChart(this.smartCoinFlowChart);
    this.smartCoinFlowChart = null;

    const canvas = this.smartCoinFlowChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color') || '#e2e8f0';

    const historical = this.smartCoinChartData.historical;
    const labels = historical.map(item => {
      if (item.date) {
        const date = new Date(item.date);
        return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
      } else if (item.month) {
        const [year, month] = item.month.split('-');
        return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });
      }
      return '';
    });

    this.smartCoinFlowChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Eingenommen',
            // ✅ Round to 2 decimal places
            data: historical.map(item => Math.round((item.earned / 100) * 100) / 100),
            backgroundColor: '#10b981',
            borderRadius: 6,
            borderSkipped: false
          },
          {
            label: 'Ausgegeben',
            // ✅ Round to 2 decimal places
            data: historical.map(item => Math.round((-item.spent / 100) * 100) / 100),
            backgroundColor: '#ef4444',
            borderRadius: 6,
            borderSkipped: false
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            align: 'end',
            labels: {
              color: textColor,
              font: { size: 11, },
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 15,
              boxWidth: 6,
              boxHeight: 6
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1,
            callbacks: {
              label: (context) => `${context.dataset.label}: ${Math.abs(context.parsed.y!).toFixed(2)} €`
            }
          }
        },
        scales: {
          x: {
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            ticks: {
              color: textColor,
              font: { size: 11 },
              callback: (value) => `${(value as number).toFixed(2)} €`
            },
            grid: {
              color: surfaceBorder,
            },
            border: {
              display: false
            }
          }
        }
      }
    });
  }

  initEngagementCharts(): void {
    if (!this.engagementData) return;
    this.initEngagementChart();
    this.initFunnelChart();
    this.initSessionDurationChart();
  }

  initEngagementChart(): void {
    if (!this.engagementChartRef?.nativeElement || !this.engagementData) return;

    this.destroyChart(this.engagementChart);
    this.engagementChart = null;

    const canvas = this.engagementChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color') || '#e2e8f0';

    const labels = this.engagementData.engagementChart.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
    });

    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, 'rgba(99, 102, 241, 0.12)');
    gradient.addColorStop(1, 'rgba(99, 102, 241, 0)');

    this.engagementChart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Engagement Events',
          data: this.engagementData.engagementChart.map(item => item.events),
          borderColor: '#6366f1',
          backgroundColor: gradient,
          borderWidth: 2.5,
          tension: 0.4,
          fill: true,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#6366f1',
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1,
            displayColors: false
          }
        },
        scales: {
          x: {
            ticks: {
              color: textColor,
              font: { size: 11, }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              color: surfaceBorder,
            },
            border: {
              display: false
            }
          }
        }
      }
    });
  }

  initFunnelChart(): void {
    if (!this.funnelChartRef?.nativeElement || !this.engagementData) return;

    this.destroyChart(this.funnelChart);
    this.funnelChart = null;

    const canvas = this.funnelChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';

    const profileVisits = this.engagementData.profileVisits.total;
    const productViews = this.engagementData.productViewCount;
    const addToCart = this.engagementData.addToCartCount;
    const orders = this.salesData?.totalOrders || 0;

    this.funnelChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Besuche', 'Produkt-Views', 'Warenkorb', 'Bestellungen'],
        datasets: [{
          data: [profileVisits, productViews, addToCart, orders],
          backgroundColor: ['#8b5cf6', '#6366f1', '#3b82f6', '#10b981'],
          borderRadius: 6,
          borderSkipped: false,
          barThickness: 32
        }]
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1,
            displayColors: false
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          }
        }
      }
    });
  }

  initSessionDurationChart(): void {
    if (!this.sessionDurationChartRef?.nativeElement || !this.engagementData) return;

    this.destroyChart(this.sessionDurationChart);
    this.sessionDurationChart = null;

    const canvas = this.sessionDurationChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';

    const sessionDurations = this.engagementData.sessionDurationDistribution || [];

    if (sessionDurations.length === 0) {
      return;
    }

    const labels = sessionDurations.map(item => item.bucket);
    const percentages = sessionDurations.map(item => item.percentage);

    this.sessionDurationChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'Sessions (%)',
          data: percentages,
          backgroundColor: '#ec4899',
          borderRadius: 6,
          borderSkipped: false
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1,
            displayColors: false,
            callbacks: {
              label: (context) => {
                const item = sessionDurations[context.dataIndex];
                return `${item.count} Sessions (${item.percentage.toFixed(1)}%)`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              color: textColor,
              font: { size: 11 },
              callback: (value) => `${value}%`
            },
            grid: {
              color: '#e2e8f0',
            },
            border: {
              display: false
            }
          }
        }
      }
    });
  }

  initDonationChart(): void {
    if (!this.donationChartRef?.nativeElement || !this.donationChartData) return;

    this.destroyChart(this.donationChart);
    this.donationChart = null;

    const canvas = this.donationChartRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color') || '#64748b';
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color') || '#e2e8f0';

    const historical = this.donationChartData.historical;
    const labels = historical.map((item: any) => {
      const date = new Date(item.date);
      return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
    });

    const donationAmounts = historical.map((item: any) => item.amount_in_euros);

    this.donationChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'Spenden (€)',
          data: donationAmounts,
          backgroundColor: '#ef4444',
          borderRadius: 6,
          borderSkipped: false
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            titleColor: '#f1f5f9',
            bodyColor: '#cbd5e1',
            padding: 12,
            borderColor: '#334155',
            borderWidth: 1,
            displayColors: false,
            callbacks: {
              label: (context) => `Spenden: ${context.parsed.y!.toFixed(2)} €`,
              afterLabel: (context) => {
                const index = context.dataIndex;
                const donorCount = historical[index].donor_count;
                return `Spender: ${donorCount}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: {
              color: textColor,
              font: { size: 11 }
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: textColor,
              font: { size: 11 },
              callback: (value) => `${(value as number).toFixed(2)} €`
            },
            grid: {
              color: surfaceBorder,
            },
            border: {
              display: false
            }
          }
        }
      }
    });
  }

  destroyCharts(): void {
    this.destroyChart(this.revenueChart);
    this.destroyChart(this.smartCoinBalanceChart);
    this.destroyChart(this.smartCoinFlowChart);
    this.destroyChart(this.engagementChart);
    this.destroyChart(this.funnelChart);
    this.destroyChart(this.sessionDurationChart);
    this.destroyChart(this.donationChart);

    this.revenueChart = null;
    this.smartCoinBalanceChart = null;
    this.smartCoinFlowChart = null;
    this.engagementChart = null;
    this.funnelChart = null;
    this.sessionDurationChart = null;
    this.donationChart = null;
  }

  // ========================================
  // HELPER METHODS
  // ========================================

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  }

  formatDate(date: string | Date): string {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  }

  formatDuration(seconds: number): string {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  }

  getTransactionTypeLabel(type: string): string {
    return this.smartCoinAnalytics.formatTransactionType(type);
  }

  getTransactionColor(type: string): 'success' | 'danger' | 'warning' | 'info' {
    return this.smartCoinAnalytics.getTransactionColor(type);
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warning' | 'danger' {
    const map: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
      'completed': 'success',
      'pending': 'warning',
      'confirmed': 'info',
      'cancelled': 'danger'
    };
    return map[status.toLowerCase()] || 'info';
  }

  getBadgeHealthColor(health: number): string {
    if (health >= 75) return 'text-emerald-600';
    if (health >= 50) return 'text-blue-600';
    if (health >= 25) return 'text-amber-600';
    return 'text-red-600';
  }

  calculateBadgeHealth(): number {
    if (!this.badgeOverview) return 0;
    return this.badgeAnalytics.calculateBadgeHealth(
      this.badgeOverview.totalAchieved,
      this.badgeOverview.totalBadges,
      this.badgeOverview.totalUsers
    );
  }

  calculateBusinessScore(): number {
    // Calculate a business performance score based on various metrics
    let score = 0;
    let factors = 0;

    // Revenue factor (max 30 points)
    if (this.salesData && this.salesData.totalRevenue > 0) {
      // Scale: 0-1000€ = 0-30 points
      const revenueScore = Math.min(30, (this.salesData.totalRevenue / 1000) * 30);
      score += revenueScore;
      factors++;
    }

    // Orders factor (max 20 points)
    if (this.salesData && this.salesData.totalOrders > 0) {
      const orderScore = Math.min(20, this.salesData.totalOrders * 2);
      score += orderScore;
      factors++;
    }

    // Engagement factor (max 25 points)
    if (this.engagementData && this.engagementData.profileVisits.total > 0) {
      const engagementScore = Math.min(25, (this.engagementData.profileVisits.total / 100) * 25);
      score += engagementScore;
      factors++;
    }

    // Badge factor (max 25 points)
    if (this.badgeOverview && this.badgeOverview.totalAchieved > 0) {
      const badgeScore = Math.min(25, (this.badgeOverview.achievementRate / 100) * 25);
      score += badgeScore;
      factors++;
    }

    // If no data yet, return 0
    if (factors === 0) return 0;

    // Normalize to 100
    const maxPossible = factors * 25; // Average max per factor
    return Math.round((score / maxPossible) * 100);
  }

  viewOrder(order: RecentOrder): void {
    this.router.navigate(['/dashboard/orders'], { queryParams: { id: order.id } });
  }

  viewProduct(product: TopProduct): void {
    this.router.navigate(['/dashboard/products'], { queryParams: { id: product.id } });
  }

  goToOrders(): void {
    this.router.navigate(['/dashboard/orders']);
  }

  goToProducts(): void {
    this.router.navigate(['/dashboard/products']);
  }

  goToPosts(): void {
    this.router.navigate(['/dashboard/posts']);
  }

  goToShop(): void {
    this.router.navigate(['/dashboard/shop/overview']);
  }

  goToBadges(): void {
    this.router.navigate(['/dashboard/badges']);
  }

  goToTickets(): void {
    this.router.navigate(['/dashboard/shop/tickets']);
  }

  // ESG Score Methods
  updateEsgScore(): void {
    // Environment: placeholder (could connect to sustainability features)
    const environmentScore = 75;

    // Social: based on engagement and community metrics
    let socialScore = 70;
    if (this.engagementData) {
      const visits = this.engagementData.profileVisits.total;
      const engagement = this.engagementData.addToCartRate;
      socialScore = Math.min(100, Math.round(50 + (visits / 10) + (engagement * 2)));
    }

    // Governance: based on badge achievement rate
    let governanceScore = 70;
    if (this.badgeOverview && this.badgeOverview.totalUsers > 0) {
      governanceScore = Math.min(100, Math.round(this.badgeOverview.achievementRate));
    }

    this.esgScore = {
      environment: environmentScore,
      social: socialScore,
      governance: governanceScore
    };
  }

  onEsgHeroClick(): void {
    // Navigate to ESG/sustainability page when available
    console.log('ESG Hero clicked');
  }

  onEsgCategoryClick(category: 'environment' | 'social' | 'governance'): void {
    console.log('ESG Category clicked:', category);
    // Could navigate to specific ESG category pages
  }
}